import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, can, avText } from "../data/constants";
import logoSvg from "../assets/UpdatedLogo/LOGO.svg";
import logoTextSvg from "../assets/updatedLogo.svg";

import activeDash from "../assets/sidebar/ActiveDashboard.svg";
import inactiveDash from "../assets/sidebar/UnActiveDashboard.svg";
import activeProd from "../assets/sidebar/ActiveProducts.svg";
import inactiveProd from "../assets/sidebar/UnActiveProducts.svg";
import activeUser from "../assets/sidebar/ActiiveUsers.svg";
import inactiveUser from "../assets/sidebar/UnActiveUser.svg";
import activeBlog from "../assets/sidebar/ActiveBlog.svg";
import inactiveBlog from "../assets/sidebar/UnActiveBlog.svg";

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const storedUser = (() => {
    try {
      const data = localStorage.getItem("user_details");
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  })();
  const userRole = storedUser?.role || user?.role;

  const items = [
    { path: "/dashboard", label: "Dashboard", activeIcon: activeDash, inactiveIcon: inactiveDash, perm: "dash" },
    { path: "/products",  label: "Products",  activeIcon: activeProd, inactiveIcon: inactiveProd, perm: "products" },
    { path: "/users",     label: "User",      activeIcon: activeUser, inactiveIcon: inactiveUser, perm: "users" },
    { path: "/zytama-users", label: "Zytama Users", activeIcon: activeUser, inactiveIcon: inactiveUser, perm: "users" },
    { path: "/blogmanage", label: "Manage Blogs", activeIcon: activeBlog, inactiveIcon: inactiveBlog, perm: "dash" },
  ].filter((n) => {
    if (n.path === "/users" || n.path === "/zytama-users" || n.path === "/blogmanage" || n.path ==="/dashboard") {
      return userRole === "admin";
    }
    return can(userRole, n.perm);
  });

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <>
      {/* Mobile Sidebar backdrop overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40 md:hidden animate-fadeIn"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div 
        className={`fixed inset-y-0 left-0 z-50 md:relative shrink-0 transition-all duration-300 p-4 h-screen bg-[#f0f2f5] ${
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`} 
        style={{ width: (collapsed && !mobileOpen) ? 110 : 280 }}
      >
        <div className="bg-white rounded-[24px] h-full shadow-sm flex flex-col overflow-hidden" style={{ border: "1px solid #0000001A" }}>
          {/* Logo Section */}
          <div className={`pt-8 pb-6 shrink-0 flex items-center min-h-[64px] ${collapsed && !mobileOpen ? 'justify-center' : 'justify-start pl-8'}`}>
            <div className="flex items-end gap-[6px] shrink-0">
              <img src={logoSvg} alt="Zytama Logo" className="h-[38px] w-auto block shrink-0" />
              {(!collapsed || mobileOpen) && (
                <img src={logoTextSvg} alt="Zytama" className="h-[28px] w-auto block" />
              )}
            </div>
          </div>

          {/* Nav items */}
          <div className="flex-1 overflow-y-auto px-4 py-2 flex flex-col gap-2">
            {items.map((n) => {
              const active = isActive(n.path);
              return (
                <button
                  key={n.path}
                  onClick={() => {
                    navigate(n.path);
                    if (setMobileOpen) setMobileOpen(false);
                  }}
                  className={`flex items-center rounded-xl px-4 py-3.5 transition-colors ${
                    active 
                      ? "bg-[#0d5f66] text-white font-medium shadow-sm" 
                      : "hover:bg-gray-50 text-[#5E5E5E] font-medium bg-transparent"
                  } ${collapsed && !mobileOpen ? "justify-center px-0" : "justify-start"}`}
                  title={collapsed && !mobileOpen ? n.label : ""}
                >
                  <img 
                    src={active ? n.activeIcon : n.inactiveIcon} 
                    alt={n.label} 
                    className="w-[22px] h-[22px] shrink-0" 
                  />
                  {(!collapsed || mobileOpen) && <span className="ml-4 text-[15px]">{n.label}</span>}
                </button>
              );
            })}
          </div>

          {/* Account / Bottom */}
          <div className="shrink-0 mt-auto border-t" style={{ borderColor: "#0000001A" }}>
            {(!collapsed || mobileOpen) ? (
              <div className="px-4 py-5 flex items-center gap-3">
                <div className="w-[46px] h-[46px] rounded-full bg-[#0d5f66] text-white flex items-center justify-center text-lg font-medium shrink-0">
                  {avText(user.name)}
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-[17px] font-bold text-black leading-tight break-words">
                    {user.name}
                  </div>
                  <div className="text-[15px] text-gray-800 truncate mt-0.5">
                    {ROLE_LABELS[userRole] || "Senior Administrator"}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 flex justify-center">
                <div className="w-[46px] h-[46px] rounded-full bg-[#0d5f66] text-white flex items-center justify-center text-lg font-medium shrink-0">
                  {avText(user.name)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
